import {
  PROJECTS,
  PROJECT_RULES,
  SHIP_CLASSES,
  TRADE_GOODS,
  type ProjectId,
  type ShipClassId,
  type TradeGoodId,
} from '@sd/content';
import { HEX_DIRECTIONS, hex, hexDistance, type Hex } from './hex';
import { isPassable } from './fleet';
import type { StarSystem } from './system';

/**
 * Joint projects against crises (GDD §9). Contributions are valued in ГС;
 * the threshold is what N−1 players can put in, full cover all N. If the
 * threshold is not met by the strike, every contribution goes back
 * (a provision point, from public-goods economics). Pure helpers.
 */
export interface Contribution {
  /** Value given, ГС. */
  readonly points: number;
  readonly goods: Readonly<Partial<Record<TradeGoodId, number>>>;
  readonly science: number;
  readonly ships: readonly ShipClassId[];
}

export interface Project {
  readonly id: number;
  readonly crisisId: number;
  readonly kind: ProjectId;
  /** Where it is built: ships must fly here to be given. */
  readonly hex: Hex;
  /** Contributions close when the crisis strikes. */
  readonly deadline: number;
  /** Value that saves contributors; and value that saves everyone. */
  readonly threshold: number;
  readonly full: number;
  readonly byPlayer: Readonly<Record<string, Contribution>>;
}

export type ProjectOutcome = 'failed' | 'threshold' | 'full';

export const emptyContribution: Contribution = { points: 0, goods: {}, science: 0, ships: [] };

export const projectTotal = (p: Project): number =>
  Object.values(p.byPlayer).reduce((sum, c) => sum + c.points, 0);

/** Threshold and full cover for a lobby of `players`. */
export function projectSize(kind: ProjectId, players: number): { threshold: number; full: number } {
  const share = PROJECTS[kind].share;
  return { threshold: share * Math.max(1, players - 1), full: share * players };
}

export function projectOutcome(p: Project): ProjectOutcome {
  const total = projectTotal(p);
  if (total >= p.full - 1e-9) return 'full';
  return total >= p.threshold - 1e-9 ? 'threshold' : 'failed';
}

/** Value of a ship given to a project: its cash price plus its alloys at base price. */
export const shipValue = (cls: ShipClassId): number =>
  SHIP_CLASSES[cls].cost.cash + SHIP_CLASSES[cls].cost.alloys * TRADE_GOODS.alloys.basePrice;

export const goodsValue = (good: TradeGoodId, quantity: number): number =>
  TRADE_GOODS[good].basePrice * quantity;

export const scienceValue = (science: number): number => science * PROJECT_RULES.sciencePrice;

/** Players a crisis spares: everyone at full cover, contributors of a real part at the threshold. */
export function coveredPlayers(p: Project, players: readonly string[]): string[] {
  const outcome = projectOutcome(p);
  if (outcome === 'full') return [...players];
  if (outcome === 'failed') return [];
  const min = PROJECTS[p.kind].share * PROJECT_RULES.minCoverShare;
  return players.filter((id) => (p.byPlayer[id]?.points ?? 0) >= min - 1e-9);
}

/**
 * Sector of a hex around the star, 0..5: the hex direction it lies closest
 * to, by integer cube dot product (no trigonometry, so every client agrees).
 * Ties go to the lower direction. −1 for the centre.
 */
export function sectorOf(h: Hex): number {
  if (h.q === 0 && h.r === 0) return -1;
  const s = -h.q - h.r;
  let best = 0;
  let bestDot = -Infinity;
  HEX_DIRECTIONS.forEach((d, i) => {
    const dot = h.q * d.q + h.r * d.r + s * (-d.q - d.r);
    if (dot > bestDot) {
      bestDot = dot;
      best = i;
    }
  });
  return best;
}

/** Where a project is built: just outside the star or near the edge, in the crisis's direction. */
export function projectHex(
  system: StarSystem,
  sector: number | null,
  where: 'corona' | 'edge',
): Hex {
  const d = HEX_DIRECTIONS[sector ?? 0]!;
  const starRadius = Math.max(0, ...system.starHexes.map((h) => hexDistance(h, hex(0, 0))));
  if (where === 'edge') {
    for (let k = system.mapRadius - 1; k > starRadius; k--) {
      const h = hex(d.q * k, d.r * k);
      if (isPassable(system, h)) return h;
    }
  }
  for (let k = starRadius + 1; k < system.mapRadius; k++) {
    const h = hex(d.q * k, d.r * k);
    if (isPassable(system, h)) return h;
  }
  return hex(d.q * system.mapRadius, d.r * system.mapRadius);
}
